import React, { useState, useEffect, useRef } from 'react';
import axios from '../utils/axiosInstance';
import '../App.css';
import '../listening-blink.css';

// Component imports
import Layout from '../components/Layout';
import { useCallState } from '../context/CallStateContext';
import { usePractice } from '../context/PracticeContext';

// API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Safety net for a silent executor abort: POST /intervene returns 200 as soon as
// the in-memory signal is written, but ari_worker can still drop the intervention
// (no RTP session, no forwarding number). Without this the button would sit on
// "Weiterleitung läuft..." for the rest of the call with nothing happening.
// Sized above the worst-case time-to-effect: the signal is only polled at turn
// boundaries, so a slow LLM turn plus TTS playback has to elapse first. On the
// success path this never fires — the forward leaves Stasis and call_ended nulls
// out activeCall long before it.
const INTERVENTION_RESET_MS = 30000;

// Shared by the number row and the intervene button so they render as one aligned
// control block. Sized to fit "Eingreifen & Weiterleiten" without wrapping.
const INTERVENTION_CONTROL_WIDTH = '240px';

const CallMonitorPage = () => {
  // Use shared context for call state (WebSocket-based real-time updates)
  const { activeCall, conversation, error, lastUpdate, setError } = useCallState();
  const { practiceId } = usePractice();

  // Local state for intervention only
  const [isIntervening, setIsIntervening] = useState(false);
  // The takeover target is a PRACTICE setting, not a per-call one — it loads on mount
  // and survives calls starting and ending, so staff can set it up in advance.
  // `savedNumber` is what the backend actually holds (a custom number, else the
  // practice's Weiterleitungsnummer) and is what the intervene button will dial;
  // `numberInput` is the editable field. They diverge only while there are unsaved
  // edits, which the UI calls out rather than silently dialling the stale one.
  const [savedNumber, setSavedNumber] = useState('');
  const [numberInput, setNumberInput] = useState('');
  const [isCustomNumber, setIsCustomNumber] = useState(false);
  const [isSavingNumber, setIsSavingNumber] = useState(false);

  // Refs
  const conversationEndRef = useRef(null);

  // Reset intervention state when call ends
  useEffect(() => {
    if (!activeCall) {
      setIsIntervening(false);
    }
  }, [activeCall]);

  // Load the takeover number per PRACTICE, not per call — no activeCall in the deps,
  // so it is available before any call arrives and a call starting can never clobber
  // what staff are typing.
  useEffect(() => {
    if (!practiceId) {
      setSavedNumber('');
      setNumberInput('');
      setIsCustomNumber(false);
      return;
    }

    let cancelled = false;
    axios.get(`${API_BASE_URL}/call-monitor/practice/${practiceId}/intervention-number`)
      .then(res => {
        if (cancelled) return;
        const number = res.data?.phone_number || '';
        setSavedNumber(number);
        setNumberInput(number);
        setIsCustomNumber(Boolean(res.data?.is_custom));
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Could not load intervention number:', err);
        setSavedNumber('');
        setNumberInput('');
        setIsCustomNumber(false);
      });

    return () => { cancelled = true; };
  }, [practiceId]);

  // Save the number. Empty input clears the override and reverts to the practice's
  // Weiterleitungsnummer — the backend answers with whichever is now effective.
  const handleSaveNumber = async () => {
    if (!practiceId) return;

    setIsSavingNumber(true);
    setError('');

    try {
      const res = await axios.put(
        `${API_BASE_URL}/call-monitor/practice/${practiceId}/intervention-number`,
        { phone_number: numberInput.trim() },
      );
      const number = res.data?.phone_number || '';
      setSavedNumber(number);
      setNumberInput(number);
      setIsCustomNumber(Boolean(res.data?.is_custom));
    } catch (err) {
      console.error('Saving intervention number failed:', err);
      setError(err.response?.data?.detail || 'Nummer konnte nicht gespeichert werden');
    } finally {
      setIsSavingNumber(false);
    }
  };

  // Re-enable the button if the intervention never takes effect (see INTERVENTION_RESET_MS)
  useEffect(() => {
    if (!isIntervening) return;
    const timer = setTimeout(() => setIsIntervening(false), INTERVENTION_RESET_MS);
    return () => clearTimeout(timer);
  }, [isIntervening]);

  // Auto-scroll to bottom of conversation when it updates
  // Only scrolls if user is within 150px of bottom (prevents auto-scroll when reading history)
  useEffect(() => {
    if (conversationEndRef.current) {
      const container = conversationEndRef.current.parentElement;
      if (container && container.scrollHeight - container.scrollTop - container.clientHeight < 150) {
        conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [conversation]);

  // Handle manual intervention - play message and forward call
  const handleIntervene = async () => {
    if (!activeCall) return;

    setIsIntervening(true);
    setError('');

    try {
      // No number in the body — the backend dials whatever was last SAVED, so the
      // button can never dial something other than what the UI shows as saved.
      await axios.post(`${API_BASE_URL}/call-monitor/intervene/${activeCall.session_id}`);
      console.log('Intervention initiated successfully');
      // Success - keep button disabled until call ends (don't reset isIntervening)
    } catch (err) {
      console.error('Intervention failed:', err);
      setError(err.response?.data?.detail || 'Eingriff fehlgeschlagen');
      setIsIntervening(false); // Only reset on error so user can retry
    }
  };

  const hasUnsavedNumber = numberInput.trim() !== savedNumber;
  const canSaveNumber = hasUnsavedNumber && !isSavingNumber;
  // Gated on savedNumber, not on the input: the backend dials the saved value.
  const interventionReady = Boolean(activeCall) && Boolean(savedNumber) && !isIntervening;

  // Format message role for display
  const formatRole = (role) => {
    if (role === 'user') return 'Caller';
    if (role === 'assistant') return 'AI Assistant';
    return role;
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <Layout>
      <div className="call-monitor-main" style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 0' }}>
        <h1 className="h1" style={{ marginBottom: '1rem' }}>Call Monitor</h1>
        
        {/* Monitor Status */}
        <div className="monitor-status" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          {activeCall ? (
            <div>
              <span className="listening-blink" style={{ fontWeight: 600, fontSize: '1.2rem', color: 'var(--state-active)' }}>
                Live Call Active
              </span>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                Channel: {activeCall.channel_id} | Session: {activeCall.session_id.substring(0, 8)}...
              </div>
            </div>
          ) : (
            <span style={{ fontWeight: 500, fontSize: '1.2rem', color: '#888' }}>
              Monitoring - No active calls
            </span>
          )}
          
          {lastUpdate && (
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.5rem' }}>
              Last updated: {formatTime(lastUpdate)}
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message" style={{ marginBottom: '1.5rem' }}>
            <p>{error}</p>
          </div>
        )}

        {/* Conversation Display */}
        <div className="conversation-container card" style={{ 
          minHeight: 400, 
          maxHeight: 600, 
          width: '100%', 
          overflowY: 'auto', 
          position: 'relative',
          padding: '1rem'
        }}>
          {activeCall?.transcript_suppressed ? (
            /* Consent refused: the call stays listed above and the takeover control
               below stays usable, but the conversation itself is withheld. */
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 360,
              padding: '2rem',
              backgroundColor: '#f0f0f0',
              border: '1px dashed #c4c4c4',
              borderRadius: '4px',
              color: '#777',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                Keine Zustimmung zur Aufzeichnung
              </div>
              <div style={{ fontSize: '0.9rem', maxWidth: 420 }}>
                Der Anrufer hat der Aufzeichnung widersprochen. Das Gespräch wird nicht angezeigt.
                Die Weiterleitung an einen Mitarbeiter ist weiterhin möglich.
              </div>
            </div>
          ) : activeCall && conversation.length > 0 ? (
            <>
              <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <strong>Active Call Session</strong>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                  Started: {formatTime(activeCall.created_at)}
                </div>
              </div>
              
              {conversation.map((message, index) => (
                <div key={index} className={`message ${message.role === 'assistant' ? 'assistant' : 'user'}`}> 
                  <div className="message-header">{formatRole(message.role)}</div>
                  <div className="message-content">{message.content}</div>
                </div>
              ))}
              <div ref={conversationEndRef} />
            </>
          ) : (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              fontSize: '1.1rem',
              color: '#888',
              textAlign: 'center'
            }}>
              {activeCall ? 'Waiting for conversation to begin...' : 'No conversation taking place at the moment'}
            </div>
          )}
        </div>

        {/* Intervention: takeover number + button, centered below chatbox.
            The number is the switch — empty field means no takeover target, so the
            button stays disabled and says why. */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem',
          marginTop: '1rem'
        }}>
          {/* Number row and intervene button share INTERVENTION_CONTROL_WIDTH so the
              two line up as one control block. */}
          <div style={{ width: INTERVENTION_CONTROL_WIDTH }}>
            <label
              htmlFor="intervention-number"
              style={{ display: 'block', fontSize: '0.85rem', color: '#666', textAlign: 'center', marginBottom: '0.25rem' }}
            >
              Nummer für Eingriff
            </label>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <input
                id="intervention-number"
                type="tel"
                className="form-input"
                value={numberInput}
                onChange={e => setNumberInput(e.target.value)}
                disabled={isSavingNumber}
                placeholder="+49..."
                style={{ flex: 1, minWidth: 0, textAlign: 'center' }}
              />
              <button
                onClick={handleSaveNumber}
                disabled={!canSaveNumber}
                title={hasUnsavedNumber ? 'Nummer speichern' : 'Keine Änderung'}
                aria-label="Nummer speichern"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // Keep the 38px box so the row still totals INTERVENTION_CONTROL_WIDTH
                  width: '38px',
                  flexShrink: 0,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  // Bare icon — state is carried by the stroke colour, not a fill
                  color: canSaveNumber ? 'var(--color-main)' : '#bbb',
                  cursor: canSaveNumber ? 'pointer' : 'not-allowed'
                }}
              >
                {/* Floppy-disk save icon, matching the inline-SVG style used in
                    TasksPage/Layout (18px, 24-viewBox, currentColor stroke). */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
              </button>
            </div>
            <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', minHeight: '1rem', textAlign: 'center' }}>
              {hasUnsavedNumber ? (
                <span style={{ color: 'var(--state-warning-text)' }}>
                  Nicht gespeichert{savedNumber ? `, es gilt weiterhin ${savedNumber}` : ''}
                </span>
              ) : (
                <span style={{ color: '#999' }}>
                  {savedNumber
                    ? (isCustomNumber ? 'Eigene Nummer gespeichert' : 'Standard: Weiterleitungsnummer')
                    : 'Keine Nummer hinterlegt'}
                </span>
              )}
            </div>
          </div>

          <button
            onClick={handleIntervene}
            disabled={!interventionReady}
            style={{
              width: INTERVENTION_CONTROL_WIDTH,
              padding: '10px 0',
              backgroundColor: interventionReady ? 'var(--color-secondary)' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: interventionReady ? 'pointer' : 'not-allowed',
              opacity: interventionReady ? 1 : 0.5,
              fontWeight: 500,
              fontSize: '1rem'
            }}
          >
            {isIntervening ? 'Weiterleitung läuft...' : 'Eingreifen & Weiterleiten'}
          </button>

          {/* Say WHY the button is dead — a missing reason here cost two full test
              cycles while fixing the greyed-out button. The number-related reasons are
              already spelled out under the field, so only the call reason goes here. */}
          {!activeCall && !isIntervening && (
            <div style={{ fontSize: '0.8rem', color: '#888' }}>
              Kein aktives Gespräch
            </div>
          )}
        </div>

        {/* Call Statistics */}
        {activeCall && (
          <div className="call-stats" style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
            <div>Messages in conversation: {conversation.length}</div>
            {activeCall.user_id && <div>User ID: {activeCall.user_id}</div>}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CallMonitorPage; 